import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Printer, Download, X } from 'lucide-react';
import api from '../../api/client';

interface Props {
  type: 'book' | 'member';
  id: number;
  open: boolean;
  onClose: () => void;
}

export default function BarcodeModal({ type, id, open, onClose }: Props) {
  const { data, isLoading } = useQuery({
    queryKey: ['barcode', type, id],
    queryFn: () => api.get(`/barcode/${type}/${id}`),
    enabled: open && !!id,
  } as any);

  if (!open) return null;

  const d = (data as any)?.data;

  const handlePrint = () => {
    const win = window.open('', '_blank');
    if (!win) return;
    const barcodes = type === 'book' ? d?.barcodes : [{ image: d?.image, barcode: d?.member?.barcode }];
    const name = type === 'book' ? `${d?.book?.title}` : `${d?.member?.last_name} ${d?.member?.first_name}`;
    win.document.write(`
      <html><head><title>Barcode — ${name}</title>
      <style>
        body{font-family:sans-serif;padding:20px}
        .card{display:inline-block;border:1px solid #ddd;border-radius:8px;padding:16px;margin:8px;text-align:center}
        .title{font-size:12px;font-weight:bold;margin-bottom:4px;max-width:200px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
        .sub{font-size:11px;color:#666;margin-bottom:8px}
        img{display:block;margin:0 auto}
        @media print{body{padding:0}}
      </style></head><body>
      ${(barcodes||[]).map((b: any) => `
        <div class="card">
          <div class="title">${name||''}</div>
          ${type==='book'?`<div class="sub">#${b.copy_id||''} ${b.call_number||''}</div>`:''}
          <img src="${b.image}" width="260"/>
          <div class="sub">${b.barcode||''}</div>
        </div>`).join('')}
      </body></html>`);
    win.document.close();
    win.print();
  };

  const handleDownload = (image: string, barcode: string) => {
    const a = document.createElement('a');
    a.href = image;
    a.download = `barcode-${barcode}.png`;
    a.click();
  };

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.75)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,padding:16}}
      onClick={e => { if(e.target===e.currentTarget) onClose(); }}>
      <div style={{background:'#0d1424',border:'1px solid #1e2d45',borderRadius:16,width:'100%',maxWidth:560,maxHeight:'90vh',display:'flex',flexDirection:'column'}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'16px 20px',borderBottom:'1px solid #1e2d45',flexShrink:0}}>
          <h2 style={{fontSize:15,fontWeight:600,color:'#f1f5f9',margin:0}}>ბარკოდი</h2>
          <div style={{display:'flex',gap:8}}>
            <button onClick={handlePrint}
              style={{display:'flex',alignItems:'center',gap:5,padding:'6px 12px',background:'#1d4ed8',border:'none',borderRadius:8,color:'white',fontSize:12,cursor:'pointer'}}>
              <Printer size={13} /> ბეჭდვა
            </button>
            <button onClick={onClose} style={{background:'none',border:'none',color:'#475569',cursor:'pointer',padding:4}}>
              <X size={18} />
            </button>
          </div>
        </div>

        <div style={{padding:20,overflowY:'auto',flex:1}}>
          {isLoading && <p style={{textAlign:'center',color:'#475569',padding:32}}>იტვირთება...</p>}

          {type==='book' && d?.barcodes?.map((b: any, i: number) => (
            <div key={i} style={{background:'#080c14',border:'1px solid #1e2d45',borderRadius:10,padding:16,marginBottom:10,display:'flex',alignItems:'center',gap:16}}>
              <img src={b.image} alt={b.barcode} style={{borderRadius:6,background:'white',padding:4,flexShrink:0,maxWidth:200}} />
              <div style={{flex:1}}>
                <div style={{fontSize:13,color:'#cbd5e1',fontWeight:500}}>{d?.book?.title}</div>
                <div style={{fontSize:11,color:'#475569',marginTop:2}}>ასლი #{b.copy_id} {b.call_number?`· ${b.call_number}`:''}</div>
                <div style={{fontSize:11,color:'#334155',fontFamily:'monospace',marginTop:4}}>{b.barcode}</div>
                <button onClick={() => handleDownload(b.image, b.barcode)}
                  style={{display:'inline-flex',alignItems:'center',gap:4,marginTop:8,padding:'4px 10px',background:'#0f2044',border:'1px solid #1e3a6e',borderRadius:6,fontSize:11,color:'#60a5fa',cursor:'pointer'}}>
                  <Download size={11} /> გადმოწერა
                </button>
              </div>
            </div>
          ))}

          {type==='member' && d && (
            <div style={{background:'#080c14',border:'1px solid #1e2d45',borderRadius:10,padding:20,textAlign:'center'}}>
              <div style={{fontSize:14,fontWeight:600,color:'#cbd5e1',marginBottom:4}}>{d?.member?.last_name} {d?.member?.first_name}</div>
              <div style={{fontSize:11,color:'#475569',marginBottom:12}}>წევრის ბარკოდი</div>
              <img src={d?.image} alt={d?.member?.barcode} style={{borderRadius:6,background:'white',padding:8,margin:'0 auto',display:'block',maxWidth:280}} />
              <div style={{fontSize:12,color:'#334155',fontFamily:'monospace',marginTop:8}}>{d?.member?.barcode}</div>
              <button onClick={() => handleDownload(d?.image, d?.member?.barcode)}
                style={{display:'inline-flex',alignItems:'center',gap:4,marginTop:12,padding:'6px 14px',background:'#0f2044',border:'1px solid #1e3a6e',borderRadius:6,fontSize:12,color:'#60a5fa',cursor:'pointer'}}>
                <Download size={12} /> გადმოწერა
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
